// TelcoTrack - Map constants (single source of truth)
// Used by environment config and map component.

/** Center of Czech Republic [lat, lng] – default map center when no saved view */
export const MAP_CENTER_CZ: [number, number] = [49.7437, 15.3386];

/** Tolerance kliku na mapě (m) – pokud je objekt do této vzdálenosti, otevře se jeho popup (jako na mobilu). */
export const CLICK_TOLERANCE_M = 3;

export const MAP_ZOOM = {
  defaultZoom: 7,
  maxZoom: 22,
  minZoom: 5,
  /** Max zoom when fitting polygon bounds */
  fitBoundsMaxZoomPolygon: 15,
  /** Max zoom when fitting object bounds or centering on point */
  fitBoundsMaxZoomObject: 18,
  /** Objects load and show at zoom >= this */
  objectLoadMinZoom: 12,
  /** DMVS TI WMS (sítě) — skip country/region zoom; tiles would be huge and unreadable. */
  dtmTiMinZoom: 12,
  /** DMVS TI GetMap is useful through ~18; overzoom above instead of empty high-z requests. */
  dtmTiMaxNativeZoom: 18,
  /** Territorial GeoJSON overlays — not at country zoom (too much data). No max zoom. */
  territoryOverlayMinZoom: 11,
  /** Address points and INFO callouts render at zoom >= this */
  addressPointsMinZoom: 18,
  /** Sector/polygon popup is shown when zoom <= this */
  polygonPopupMaxZoom: 15,
  /**
   * Esri World Imagery: many CZ rural tiles already return the small
   * "Map data not yet available" JPEG at LOD 19 (HTTP 200), while cities still have real z19.
   * Cap Leaflet `maxNativeZoom` at 18 so the map overzooms the last real tile through z22
   * instead of painting blank placeholders (and instead of requesting non-existent z20+).
   */
  esriWorldImageryMaxNativeZoom: 18,
  /** OSM public tile servers typically stop at 19. */
  osmMaxNativeZoom: 19,
  /**
   * ČÚZK INSPIRE CP WMS is reliable through ~19; above that request failures used to trip
   * satellite→OSM fallback and made basemap switching feel broken after XYZ overzoom.
   */
  cuzkCadastralWmsMaxNativeZoom: 19,
} as const;

/**
 * Leaflet interaction — inertial pan (mapy.com-like) + integer wheel zoom.
 * Fractional `zoomSnap: 0` blurred tiles, desynced pinch/wheel, and skipped object
 * loads on half-levels. SVG renderer (Leaflet default) — `preferCanvas` hid the
 * pointer over point markers (full-map canvas hover + cursor).
 * Object-load / address / polygon-popup thresholds still use `MAP_ZOOM`.
 */
export const MAP_LEAFLET_INTERACTION = {
  scrollWheelZoom: true,
  /** Snap to whole levels after wheel/pinch so tiles and bbox loads stay aligned. */
  zoomSnap: 1,
  /** +/- control step; keep 1 so it matches integer `zoomSnap`. */
  zoomDelta: 1,
  /**
   * Wheel pixels per zoom level (Leaflet default 60). Higher = less sensitive.
   * 130 ≈ 2× default so a trackpad/wheel flick does not skip several integer levels.
   * Pinch (`touchZoom`) and +/- buttons (`zoomDelta`) are unchanged.
   */
  wheelPxPerZoomLevel: 130,
  /** Leaflet default 40; keep first-tick latency low without stacking trackpad bursts. */
  wheelDebounceTime: 40,
  /** Pinch uses gesture scale 1:1; snap still applies at gesture end. */
  touchZoom: true,
  inertia: true,
  /** Slightly longer coast than Leaflet’s 3000 default. */
  inertiaDeceleration: 2500,
  inertiaMaxSpeed: 2000,
  easeLinearity: 0.2,
  zoomAnimation: true,
  fadeAnimation: true,
  markerZoomAnimation: true,
  bounceAtZoomLimits: false,
} as const;

/** Keep tiles on-screen during pan; scale existing tiles during zoom (no mid-zoom refetch). */
export const MAP_TILE_SMOOTH = {
  updateWhenIdle: false,
  updateWhenZooming: false,
  keepBuffer: 4,
} as const;

/**
 * Must match backend `BBOX_MAX_AREA_SQ_DEG` (`object-service` spatial.config).
 * GET /objects/search/bbox rejects larger envelopes — web skips the request (no load-error UI).
 */
export const BBOX_MAX_AREA_SQ_DEG = 0.25;

/** Viewport padding for object bbox requests (each side). 1.5 → 4× span / 16× area. */
export const OBJECT_BBOX_BUFFER_MULTIPLIER = 1.5;

/**
 * Segment selection (cursor mapping / partial phase advance).
 * We operate on a single object's geometry (LineString or MultiLineString from DB).
 */
export const SEGMENT_SELECTION = {
  /**
   * Max distance (in degree units) between the end of one segment and the start of the next
   * to consider them "connected" within one object's MultiLineString.
   * ~0.00002 deg ≈ 2 m at mid-latitudes. Keeps frontend connectivity aligned with backend.
   */
  gapTolerance: 0.00002,
  /**
   * Max number of points to pass to the preview/confirmed polyline for drawing.
   * Long paths are subsampled so the line always renders; full chain is still sent to backend.
   */
  maxPointsForDrawing: 500,
  /**
   * Before first click: if snap point is farther than this (degrees) from cursor, allow switching to another chain.
   * ~0.0002 deg ≈ 20 m. Keeps yellow circle on one line until cursor moves away.
   */
  maxHoverDistanceToKeepChain: 0.0002,
  /**
   * When two path segments are within this distance (squared, degree²) of the cursor, prefer the one
   * closer to the current path position so the snap does not jump to another part of the path.
   * ~(0.00015)² ≈ (15 m)². Prevents "imaginary lines" when path crosses or runs parallel.
   */
  snapStickinessDistSq: 0.00015 * 0.00015,
} as const;
